import { supabase } from '../utils/supabaseClient';
import { DomainInfo, SubdomainCheckResult, CustomDomainStatus, DnsRecord } from '../types/domain';

const API_BASE = '/api/domains';

export const domainService = {
  /**
   * Get domain info for current tenant
   */
  async getDomainInfo(): Promise<DomainInfo> {
    try {
      const response = await fetch(API_BASE, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
      });

      if (!response.ok) {
        // Check if response is JSON before parsing
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const error = await response.json();
          throw new Error(error.error || 'Failed to fetch domain information');
        }
        throw new Error(`Domain service unavailable (${response.status})`);
      }

      return response.json();
    } catch (error) {
      if (error instanceof SyntaxError) {
        throw new Error('Domain service is not available. Please ensure the backend server is running.');
      }
      throw error;
    }
  },

  /**
   * Check if subdomain is available
   */
  async checkSubdomainAvailability(subdomain: string): Promise<SubdomainCheckResult> {
    const response = await fetch(`${API_BASE}/check-subdomain/${encodeURIComponent(subdomain)}`, {
      headers: {
        'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to check subdomain availability');
    }

    return response.json();
  },

  /**
   * Generate subdomain suggestion
   */
  async generateSubdomain(data: { email?: string; name?: string }): Promise<{ suggested: string }> {
    const response = await fetch(`${API_BASE}/generate-subdomain`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to generate subdomain');
    }

    return response.json();
  },

  /**
   * Update tenant subdomain
   */
  async updateSubdomain(subdomain: string): Promise<DomainInfo> {
    const response = await fetch(`${API_BASE}/subdomain`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
      },
      body: JSON.stringify({ subdomain }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update subdomain');
    }

    return response.json();
  },

  /**
   * Add custom domain
   */
  async addCustomDomain(customDomain: string): Promise<CustomDomainStatus> {
    const response = await fetch(`${API_BASE}/custom-domain`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
      },
      body: JSON.stringify({ customDomain }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to add custom domain');
    }

    return response.json();
  },

  /**
   * Verify custom domain DNS records
   */
  async verifyCustomDomain(): Promise<CustomDomainStatus> {
    const response = await fetch(`${API_BASE}/verify-custom-domain`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to verify custom domain');
    }

    return response.json();
  },

  /**
   * Remove custom domain
   */
  async removeCustomDomain(): Promise<void> {
    const response = await fetch(`${API_BASE}/custom-domain`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to remove custom domain');
    }
  },

  /**
   * Get DNS records for custom domain
   */
  async getDnsRecords(): Promise<{ dnsRecords: DnsRecord[] }> {
    const response = await fetch(`${API_BASE}/dns-records`, {
      headers: {
        'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch DNS records');
    }

    return response.json();
  },
};
